import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type RefObject } from "react";
import {
    buildPanelDragBounds,
    buildSnapAnchors,
    computeDragHeightFromPointerGrab,
    measureRenderedPanelHeightVh,
    resolveDragStartHeightVh,
    resolveLowestAllowedSnapTier,
    resolveNearestSnapTier,
    resolveSnapTierHeightVh,
    resolveViewportHeightPx,
    shouldSkipDragHeightUpdate,
} from "./panelResize.tools";
import type {
    PanelResizeLayout,
    PanelResizeProfile,
    PanelResizeStoreAdapter,
    PanelSnapTier,
} from "./panelResize.types";

export interface UsePanelDragSessionParams {
    "profile":      PanelResizeProfile;
    "layoutBounds": PanelResizeLayout;
    "adapter":      PanelResizeStoreAdapter;
    "containerRef": RefObject<HTMLDivElement | null>;
}

export interface PanelDragHandleProps {
    "onPointerDown":   ( event: React.PointerEvent<HTMLDivElement> ) => void;
    "onPointerMove":   ( event: React.PointerEvent<HTMLDivElement> ) => void;
    "onPointerUp":     ( event: React.PointerEvent<HTMLDivElement> ) => void;
    "onPointerCancel": ( event: React.PointerEvent<HTMLDivElement> ) => void;
    "onKeyDown":       ( event: React.KeyboardEvent<HTMLDivElement> ) => void;
    "style":           React.CSSProperties;
    "tabIndex":        number;
    "role":            "slider";
    "aria-label":      string;
    "aria-valuemin":   number;
    "aria-valuemax":   number;
    "aria-valuenow":   number;
}

export interface UsePanelDragSessionResult {
    "handleProps":  PanelDragHandleProps;
    "isDragActive": boolean;
    "liveHeightVh": number;
    "handleRef":    RefObject<HTMLDivElement | null>;
}

/**
 * Manages pointer and keyboard resize sessions for a {@link PanelResizeHandle}.
 *
 * @param params - Profile, layout bounds, store adapter, and panel container ref.
 * @returns Handle props, drag state, and live height for a11y.
 */
export const usePanelDragSession = ( params: UsePanelDragSessionParams ): UsePanelDragSessionResult => {
    const { profile, layoutBounds, adapter, containerRef } = params;

    const handleRef = useRef<HTMLDivElement | null>( null );
    const activePointerIdRef = useRef<number | null>( null );
    const pointerOffsetFromPanelTopPx = useRef<number>( 0 );
    const dragStartHeightVh = useRef<number>( 0 );
    const lastAppliedHeightVh = useRef<number | null>( null );
    const rafId = useRef<number | null>( null );
    const isDragActiveRef = useRef<boolean>( false );

    const [ isDragActive, setIsDragActive ] = useState( false );

    const commitTier = useCallback( ( tier: PanelSnapTier, maximizedHeightVh?: number ): void => {
        const resolvedMaximizedHeightVh = maximizedHeightVh ?? layoutBounds.resolveMaximizedPanelHeightVh();
        const anchors = buildSnapAnchors( profile, resolvedMaximizedHeightVh );
        const heightVh = resolveSnapTierHeightVh( tier, anchors );
        adapter.commitSnapTier( tier, heightVh, profile.profileId );
    }, [ adapter, layoutBounds, profile ] );

    const cancelScheduledFrame = useCallback( (): void => {
        if ( rafId.current !== null ) {
            cancelAnimationFrame( rafId.current );
            rafId.current = null;
        }
    }, [] );

    const finishDrag = useCallback( (): void => {
        isDragActiveRef.current = false;
        activePointerIdRef.current = null;
        setIsDragActive( false );
        cancelScheduledFrame();
        lastAppliedHeightVh.current = null;
    }, [ cancelScheduledFrame ] );

    /**
     * Releases pointer capture only when this handle still owns the given pointer.
     *
     * @remarks
     * Capture may already be lost after `pointercancel`, a DOM remount during drag, an implicit
     * browser release on `pointerup`, or a race between cleanup and the ending event — calling
     * `releasePointerCapture` unconditionally (or after a stale `hasPointerCapture`) throws.
     *
     * @param pointerId - Pointer id from the ending pointer event.
     */
    const releaseCaptureIfHeld = useCallback( ( pointerId: number ): void => {
        const handleElement = handleRef.current;

        if ( handleElement !== null && handleElement.hasPointerCapture( pointerId ) ) {
            try {
                handleElement.releasePointerCapture( pointerId );
            } catch {
                // Browser already released capture; safe to ignore.
            }
        }

        if ( activePointerIdRef.current === pointerId ) {
            activePointerIdRef.current = null;
        }
    }, [] );

    const applyLiveHeight = useCallback( ( heightVh: number ): void => {
        if ( shouldSkipDragHeightUpdate(
            lastAppliedHeightVh.current,
            heightVh,
            profile.dragHeightUpdateEpsilonVh,
        ) ) {
            return;
        }

        lastAppliedHeightVh.current = heightVh;
        adapter.updateDragHeight( heightVh );
    }, [ adapter, profile.dragHeightUpdateEpsilonVh ] );

    const onPointerDown = useCallback( ( event: React.PointerEvent<HTMLDivElement> ): void => {
        event.preventDefault();

        const container = containerRef.current;
        if ( container === null ) {
            return;
        }

        const viewportHeightPx = resolveViewportHeightPx();
        const containerRect = container.getBoundingClientRect();
        const renderedHeightVh = measureRenderedPanelHeightVh( container, viewportHeightPx );
        const maximizedHeightVh = layoutBounds.resolveMaximizedPanelHeightVh();
        const startHeightVh = resolveDragStartHeightVh( {
            "committedHeightVh": adapter.getCommittedHeightVh(),
            "renderedHeightVh":  renderedHeightVh,
            "maxVh":             maximizedHeightVh,
        } );

        pointerOffsetFromPanelTopPx.current = event.clientY - containerRect.top;
        dragStartHeightVh.current = startHeightVh;
        lastAppliedHeightVh.current = startHeightVh;
        isDragActiveRef.current = true;
        setIsDragActive( true );

        adapter.beginDrag( startHeightVh, profile.profileId );

        activePointerIdRef.current = event.pointerId;

        if ( handleRef.current !== null ) {
            handleRef.current.setPointerCapture( event.pointerId );
        }
    }, [ adapter, containerRef, layoutBounds, profile.profileId ] );

    const onPointerMove = useCallback( ( event: React.PointerEvent<HTMLDivElement> ): void => {
        if ( !isDragActiveRef.current || activePointerIdRef.current !== event.pointerId ) {
            return;
        }

        event.preventDefault();

        const container = containerRef.current;
        if ( container === null ) {
            return;
        }

        const viewportHeightPx = resolveViewportHeightPx();
        const panelBottomPx = container.getBoundingClientRect().bottom;
        const maximizedHeightVh = layoutBounds.resolveMaximizedPanelHeightVh();
        const bounds = buildPanelDragBounds( profile, maximizedHeightVh, viewportHeightPx );
        const nextHeightVh = computeDragHeightFromPointerGrab(
            event.clientY,
            pointerOffsetFromPanelTopPx.current,
            panelBottomPx,
            viewportHeightPx,
            bounds,
        );

        applyLiveHeight( nextHeightVh );
    }, [ applyLiveHeight, containerRef, layoutBounds, profile ] );

    const onPointerUp = useCallback( ( event: React.PointerEvent<HTMLDivElement> ): void => {
        if ( !isDragActiveRef.current || activePointerIdRef.current !== event.pointerId ) {
            return;
        }

        releaseCaptureIfHeld( event.pointerId );

        cancelScheduledFrame();

        const maximizedHeightVh = layoutBounds.resolveMaximizedPanelHeightVh();
        const anchors = buildSnapAnchors( profile, maximizedHeightVh );
        const currentHeightVh = lastAppliedHeightVh.current ?? dragStartHeightVh.current;
        const snappedTier = resolveNearestSnapTier( currentHeightVh, anchors, profile.allowedSnapTiers );
        commitTier( snappedTier, maximizedHeightVh );
        finishDrag();
    }, [ cancelScheduledFrame, commitTier, finishDrag, layoutBounds, profile, releaseCaptureIfHeld ] );

    const onPointerCancel = useCallback( ( event: React.PointerEvent<HTMLDivElement> ): void => {
        if ( !isDragActiveRef.current || activePointerIdRef.current !== event.pointerId ) {
            return;
        }

        releaseCaptureIfHeld( event.pointerId );

        cancelScheduledFrame();
        adapter.endDragWithoutCommit();
        finishDrag();
    }, [ adapter, cancelScheduledFrame, finishDrag, releaseCaptureIfHeld ] );

    const onKeyDown = useCallback( ( event: React.KeyboardEvent<HTMLDivElement> ): void => {
        if ( event.key === "ArrowUp" || event.key === "PageUp" ) {
            event.preventDefault();
            commitTier( "expanded" );

            return;
        }

        if ( event.key === "End" ) {
            event.preventDefault();
            commitTier( "maximized" );

            return;
        }

        if ( event.key === "ArrowDown" || event.key === "PageDown" || event.key === "Home" ) {
            event.preventDefault();
            commitTier( resolveLowestAllowedSnapTier( profile.allowedSnapTiers ) );
        }
    }, [ commitTier, profile.allowedSnapTiers ] );

    useEffect( () => {
        return () => {
            cancelScheduledFrame();

            const activePointerId = activePointerIdRef.current;
            if ( activePointerId !== null ) {
                releaseCaptureIfHeld( activePointerId );
            }

            if ( isDragActiveRef.current ) {
                adapter.endDragWithoutCommit();
            }
        };
    }, [ adapter, cancelScheduledFrame, releaseCaptureIfHeld ] );

    const subscribeToPanelHeight = useCallback( ( listener: () => void ) => {
        return adapter.subscribe( listener );
    }, [ adapter ] );

    const liveHeightVh = useSyncExternalStore(
        subscribeToPanelHeight,
        () => adapter.getLiveDragHeightVh() ?? adapter.getCommittedHeightVh(),
        () => adapter.getCommittedHeightVh(),
    );

    const dragBounds = useMemo( () => {
        return buildPanelDragBounds(
            profile,
            layoutBounds.resolveMaximizedPanelHeightVh(),
            resolveViewportHeightPx(),
        );
    }, [ layoutBounds, profile ] );

    const handleProps: PanelDragHandleProps = {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        "onPointerCancel": onPointerCancel,
        onKeyDown,
        "style":           { "touchAction": "none" },
        "tabIndex":        0,
        "role":            "slider",
        "aria-label":      "Redimensionner le panneau",
        "aria-valuemin":   dragBounds.chromeMinHeightVh,
        "aria-valuemax":   dragBounds.maximizedHeightVh,
        "aria-valuenow":   Math.round( liveHeightVh ),
    };

    return {
        handleProps,
        isDragActive,
        liveHeightVh,
        handleRef,
    };
};
